import { useState, FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';

export function LoginPage() {
  const { isAuthenticated, isLoading, login, confirmMFA, pendingMFA } = useAuth();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already authenticated
  const from = (location.state as { from?: Location })?.from?.pathname || '/';

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await login(email, password);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'An error occurred during login';

      // Handle common Cognito errors
      if (message.includes('NotAuthorizedException')) {
        setError('Invalid email or password');
      } else if (message.includes('UserNotFoundException')) {
        setError('User not found');
      } else if (message.includes('UserNotConfirmedException')) {
        setError('Please confirm your email address first');
      } else if (message.includes('already a signed in user') || message.includes('UserAlreadyAuthenticatedException')) {
        setError('Session conflict. Please try again.');
      } else {
        setError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMFASubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await confirmMFA(mfaCode);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid code';
      if (message.includes('CodeMismatchException') || message.includes('NotAuthorizedException')) {
        setError('Incorrect code. Check your authenticator app and try again.');
      } else if (message.includes('ExpiredCodeException')) {
        setError('Code expired. Please enter a new code from your authenticator app.');
      } else {
        setError(message);
      }
      setMfaCode('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800 px-4">
      <div className="max-w-md w-full">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4">
            <span className="text-3xl text-white">R</span>
          </div>
          <h1 className="text-2xl font-bold text-white">RightNow Admin</h1>
          <p className="text-gray-400 mt-2">
            {pendingMFA
              ? 'Two-factor authentication required'
              : 'Sign in to access the admin panel'}
          </p>
        </div>

        {pendingMFA ? (
          /* ── MFA Step ── */
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-bold text-sm">2</div>
                <h2 className="text-lg font-semibold text-gray-900">Verify your identity</h2>
              </div>
              <p className="text-sm text-gray-500">
                Enter the 6-digit code from your authenticator app, or an 8-character backup code.
              </p>
            </div>

            <form onSubmit={handleMFASubmit} className="space-y-6">
              {error && (
                <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="mfa-code" className="form-label">
                  Authentication code
                </label>
                <input
                  id="mfa-code"
                  type="text"
                  inputMode="numeric"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\s/g, ''))}
                  className="form-input text-center text-xl tracking-widest font-mono"
                  placeholder="000000"
                  maxLength={8}
                  required
                  autoComplete="one-time-code"
                  autoFocus
                  disabled={isSubmitting}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || mfaCode.length < 6}
                className="btn btn-primary w-full py-3"
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-center text-sm text-gray-500">
                Lost access to your authenticator?{' '}
                <span className="text-gray-700 font-medium">Use one of your saved backup codes.</span>
              </p>
            </div>
          </div>
        ) : (
          /* ── Password Step ── */
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="form-label">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                  placeholder="admin@example.com"
                  required
                  autoComplete="email"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  disabled={isSubmitting}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary w-full py-3"
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-center text-sm text-gray-500">
                This area is restricted to authorized administrators only.
                <br />
                If you need access, contact the platform team.
              </p>
            </div>
          </div>
        )}

        {/* Security notice */}
        <p className="text-center text-xs text-gray-500 mt-6">
          All admin actions are logged and audited.
          <br />
          Unauthorized access attempts will be reported.
        </p>
      </div>
    </div>
  );
}
